import { AutoComplete, Button, DatePicker, Drawer, Form, Input, Select, Space, Typography } from "antd";
import { observer } from "mobx-react-lite";
import layoutStorage, { Dialogs } from "../storages/layout-storage";
import ordersStorage from "../storages/orders.storage";
import { useState } from "react";
import { itemsCategories, mainOrderCategories } from "../pages/orders/data/items-categories";
import { IOrder, IOrderedItem } from "@drone-parts.garphild.pro/interfaces";

export interface AddOrderDialogProps {
  opened: boolean;
}

export function AddOrderDialog(props: AddOrderDialogProps) {
  const { opened } = props;
  const { toggleDialog } = layoutStorage;
  const [form] = Form.useForm();
  const [ sellerSearchValue, setSellerSearchValue ] = useState<string>('');
  const [ marketplaceSearchValue, setMarketplaceSearchValue ] = useState<string>('');
  const [ fieldValues, setFields ] = useState<Partial<IOrder>>({
    marketplaceId: '',
    sellerId: '',
    orderId: '',
    orderStatus: '',
    orderTotal: 0,
    orderCurrency: '',
    orderShippingMethod: '',
    orderShippingAddress: '',
    orderItems: [],
  });

  const setFieldsValue = (values: Partial<IOrder>) => {
    setFields({
      ...fieldValues,
      ...values
    });
    form.setFieldsValue(values);
  }

  const setOrderedItemFieldsValue = (index: number, part: string, value: any) => {
    const items = fieldValues.orderItems || [];
    items[index] = {
      ...items[index],
      [part]: value,
    };
    setFieldsValue({ orderItems: [...items] });
  };

  return (
    <Drawer
      title="Add Order"
      width={1020}
      closable={true}
      onClose={() => toggleDialog(Dialogs.AddOrderDialog, 'close')}
      open={opened}
      extra={(
        <Space>
          <Space>
            {fieldValues.orderItems?.reduce((acc, item) => acc + (item.itemPrice ? +item.itemPrice :  0) * (item.itemQuantity ? item.itemQuantity : 0), 0) || 0}
          </Space>
          <Button
            onClick={() => {
              ordersStorage.createOrder(fieldValues);
            }}
          >
            Create Order
          </Button>
          <Button onClick={() => {
            const items: IOrderedItem[] = form.getFieldValue('orderItems') || [];
            items.push({
              itemUrl: '',
              itemText: '',
              shortName: '',
              itemVariant: '',
              itemPrice: 0,
              itemCurrency: fieldValues.orderCurrency || 'USD',
              itemQuantity: 1,
              category: '',
              part: '',
              received: false,
              inStock: 0,
              unitsInPackage: 1,
              orderId: '',
              itemRealPrice: 0,
            });
            setFieldsValue({ orderItems:items });

          }}>
            Add Item
          </Button>
        </Space>
      )}
    >
      {/*<pre style={{position: 'fixed', top: 0, left: 0, right: '50vw', maxHeight: '90vh', overflow: "scroll", backgroundColor: 'wheat', padding: 10, zIndex: 1}}>{JSON.stringify(fieldValues, null, 2)}</pre>*/}
      <Form
        form={form}
        layout="horizontal"
        onFinish={(values) => {
          console.log(values);
        }}
        labelCol={{ span: 5 }}
      >
        <Form.Item
          label="Marketplace"
          name="marketplaceId"
        >
          <AutoComplete
            placeholder="Select a marketplace"
            onSearch={(value) => {
              setMarketplaceSearchValue(value);
              setFieldsValue({ marketplaceId: value })
            }}
            onClear={() => setMarketplaceSearchValue('')}
            onSelect={(value) => {
              setFieldsValue({ marketplaceId: value })
              setMarketplaceSearchValue('');
            }}
            options={ordersStorage
              .marketplaces
              ?.filter((marketplace) => marketplaceSearchValue ? marketplace.toLowerCase().includes(marketplaceSearchValue.toLowerCase()) : true)
              ?.map((marketplace) => ({
                value: marketplace,
                label: marketplace,
              })) || []}
          >
            <Input.Search />
          </AutoComplete>
        </Form.Item>
        <Form.Item
          label="Seller"
          name="sellerId"
        >
          <AutoComplete
            placeholder="Select a seller"
            onSearch={(value) => {
              setSellerSearchValue(value);
              setFieldsValue({ sellerId: value })
            }}
            onClear={() => setSellerSearchValue('')}
            onSelect={(value) => {
              setFieldsValue({ sellerId: value })
              setSellerSearchValue('');
            }}
            options={ordersStorage
              .sellers[form.getFieldValue('marketplaceId')]
              ?.filter((seller) => sellerSearchValue ? seller.toLowerCase().includes(sellerSearchValue.toLowerCase()) : true)
              ?.map((seller) => ({
                value: seller,
                label: seller,
              })) || []}
          >
            <Input.Search />
          </AutoComplete>
        </Form.Item>
        <Form.Item
          label="Order ID"
          name="orderId"
          rules={[{ required: true, message: "Please input order ID!" }]}
        >
          <Input
            placeholder="Order ID"
            onInput={(event) => {
              setFieldsValue({ orderId: event.currentTarget.value });
            }}
            addonAfter={
              <Button
                onClick={() => {
                  setFieldsValue({ orderId: Math.random().toString(36).slice(2) });
                }}
              >
                Generate
              </Button>
            }
          />
        </Form.Item>
        <Form.Item
          label="Order date"
          name="orderDate"
          // rules={[{ required: true, message: "Please input order date!" }]}
        >
          <DatePicker
            placeholder="Order date"
            type="date"
            allowClear
            onChange={(date) => {
              try {
                if (date) {
                  setFieldsValue({ orderDate: date });
                } else {
                  setFieldsValue({ orderDate: undefined });
                }
              } catch (e) {
                console.error(e);
                setFieldsValue({ orderDate: undefined });
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label="Order status"
          name="orderStatus"
          rules={[{ required: true, message: "Please input order status!" }]}
        >
          <Select
            placeholder="Select order status"
            onChange={(value) => {
              setFieldsValue({ orderStatus: value });
            }}
          >
            <Select.Option value="new">New</Select.Option>
            <Select.Option value="Awaiting delivery">Shipped</Select.Option>
            <Select.Option value="Completed">Delivered</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Order total"
          name="orderTotal"
          rules={[{ required: true, message: "Please input order total!" }]}
        >
          <Input
            placeholder="Order total"
            type="number"
            onInput={(event) => {
              setFieldsValue({ orderTotal: parseFloat(event.currentTarget.value) });
            }}
          />
        </Form.Item>
        <Form.Item
          label="Order currency"
          name="orderCurrency"
          rules={[{ required: true, message: "Please input order currency!" }]}
        >
          <Select
            placeholder="Select currency"
            options={[
              { value: "USD", label: "USD" },
              { value: "EUR", label: "EUR" },
              { value: "UAH", label: "UAH" },
            ]}
            onChange={(value) => {
              setFieldsValue({ orderCurrency: value });
            }}
          />
        </Form.Item>
        <Form.Item
          label="Order shipping method"
          name="orderShippingMethod"
        >
          <Input
            placeholder="Order shipping method"
            onInput={(event) => {
              setFieldsValue({ orderShippingMethod: event.currentTarget.value });
            }}
          />
        </Form.Item>
        <Form.Item
          label="Order shipping address"
          name="orderShippingAddress"
        >
          <AutoComplete
            placeholder="Select a shipping address"
            options={ordersStorage.shippingAddresses.map((address) => ({
              value: address,
              label: address,
            }))}
            onChange={(value) => {
              setFieldsValue({ orderShippingAddress: value });
            }}
          >
            <Input.Search />
          </AutoComplete>
        </Form.Item>
        <Space style={{ width: '100%'}}>
          <Typography>Items ({fieldValues.orderItems?.length || 0}) </Typography>
        </Space>
        <Space style={{display: 'flex', flexDirection: 'column'}}>
          {fieldValues?.orderItems?.map((item: IOrderedItem, index: number) => (
            <Space
              key={index}
              style={{
                width: '100%',
                border: '1px solid #f0f0f0',
                padding: 10,
                marginBottom: 10,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'space-between',
            }}
            >
              <Typography>Item {index + 1}</Typography>
              <Form.Item
                label="Category"
                name={`orderItems.${index}.category`}
                rules={[{ required: true, message: "Please input category!" }]}
              >
                <Select
                  style={{
                    minWidth: 150,
                  }}
                  onSelect={(value) => {
                    setOrderedItemFieldsValue(index, 'category', value);
                  }}
                  options={[
                    { label: "Select part", value: "" },
                    ...(mainOrderCategories || []),
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Part"
                name={`orderItems.${index}.part`}
                rules={[{ required: true, message: "Please input part!" }]}
              >
                <Select
                  style={{
                    minWidth: 150,
                  }}
                  options={[
                    { label: "Select part", value: "" },
                    ...(itemsCategories[item.category || ""] || []),
                  ]}
                  onSelect={(value) => {
                    setOrderedItemFieldsValue(index, 'part', value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Item URL"
                name={`orderItems.${index}.itemUrl`}
              >
                <Input
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'itemUrl', event.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Item text"
                name={`orderItems.${index}.itemText`}
                rules={[{ required: true, message: "Please input item text!" }]}
              >
                <Input
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'itemText', event.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Short name"
                name={`orderItems.${index}.shortName`}
              >
                <Input
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'shortName', event.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Item variant"
                name={`orderItems.${index}.itemVariant`}
              >
                <Input
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'itemVariant', event.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Units in package"
                name={`orderItems-${index}-unitsInPackage`}
                rules={[{ required: true, message: "Please input units in package!" }]}
              >
                <Input
                  type="number"
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'unitsInPackage', +event.target.value);
                    setOrderedItemFieldsValue(index, 'inStock', +event.target.value * (fieldValues.orderItems?.[index]?.itemQuantity || 0));
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Quantity"
                name={`orderItems.${index}.itemQuantity`}
                rules={[{ required: true, message: "Please input quantity!" }]}
              >
                <Input
                  type="number"
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'itemQuantity', +event.target.value);
                    setOrderedItemFieldsValue(index, 'inStock', +event.target.value * (fieldValues.orderItems?.[index]?.unitsInPackage || 0));
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Price"
                name={`orderItems.${index}.itemPrice`}
                rules={[{ required: true, message: "Please input price!" }]}
              >
                <Input
                  type="number"
                  defaultValue={fieldValues.orderItems?.[index]?.itemPrice}
                  value={fieldValues.orderItems?.[index]?.itemPrice}
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'itemPrice', +event.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Currency"
                name={`orderItems.${index}.itemCurrency`}
                rules={[{ required: true, message: "Please input currency!" }]}
              >
                <Select
                  placeholder="Select currency"
                  defaultValue={fieldValues.orderItems?.[index]?.itemCurrency}
                  value={fieldValues.orderItems?.[index]?.itemCurrency}
                  options={[
                    { value: "USD", label: "USD" },
                    { value: "EUR", label: "EUR" },
                    { value: "UAH", label: "UAH" },
                  ]}
                  onChange={(value) => {
                    setOrderedItemFieldsValue(index, 'itemCurrency', value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Received"
                name={`orderItems.${index}.received`}
              >
                <Select
                  placeholder="Select received"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  onChange={(value) => {
                    setOrderedItemFieldsValue(index, 'received', value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="In stock"
                name={`orderItems.${index}.inStock`}
                rules={[{ required: true, message: "Please input in stock!" }]}
              >
                <Input
                  type="number"
                  value={fieldValues.orderItems?.[index]?.inStock}
                  defaultValue={fieldValues.orderItems?.[index]?.inStock}
                  onChange={(event) => {
                    setOrderedItemFieldsValue(index, 'inStock', +event.target.value);
                  }}
                />
              </Form.Item>
              <hr />
            </Space>
          ))}
        </Space>
      </Form>
    </Drawer>
  );
}

export default observer(AddOrderDialog);
